import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { post } from '../api-client';
import type { InvoicePrices } from './use-prebook-selected-patient';
import { toCoPaymentWarning, type CoPaymentWarning } from './co-payment-warning';

/**
 * POST /api/v1/Practice/PreBookCalculatePrice
 *
 * Mirrors Flutter `scheduling_data_source_impl.dart:740–774`. Fired on
 * payment-type or sponsor change in the Payment section.
 *
 * Response shape (Flutter `calculate_sponsor_amount_response.dart`):
 *   { gross, discount, net, patientAmount, sponsorAmount, payable, serviceId }
 *
 * Flutter maps it onto `appointment.invoice` per `payment_widget.dart:276-283`:
 *   invoice.grossTotal          ← response.gross
 *   invoice.discountTotal       ← response.discount
 *   invoice.netTotal            ← response.net
 *   invoice.patientPayable      ← response.payable  (NOT patientAmount)
 *   invoice.sponsorOutstanding  ← response.sponsorAmount
 *
 * The React side keeps the longer Invoice-style keys (`InvoicePrices`) so
 * the breakdown UI doesn't have to know about either shape; we normalise
 * here.
 *
 * Implemented as a query (not a mutation) so react-query's cache dedupes
 * concurrent calls with the same params — without that, React 19's
 * StrictMode double-mount fires the request twice on every panel open.
 * Pass `null` while required IDs aren't ready; the query stays disabled.
 */
export interface CalculatePriceParams {
  billingProfileId?: string | null;
  locationId: string;
  patientId: string;
  sponsorId?: string | null;
  paymentType: 'Self-pay' | 'Insurance' | 'Corporate';
  providerId: string;
  individualServices?: Array<{ id: string; type: string; quantity: number }>;
}

interface RawCalculatePriceResponse {
  gross?: number;
  discount?: number;
  net?: number;
  patientAmount?: number;
  sponsorAmount?: number;
  payable?: number;
  serviceId?: string;
  coPaymentWarning?: unknown;
}

/** The normalised prices plus why there is no co-payment (PROD-3347); not part of the invoice totals. */
export type CalculatePriceResult = InvoicePrices & { coPaymentWarning: CoPaymentWarning | null };

export function useCalculatePrice(params: CalculatePriceParams | null) {
  return useQuery({
    queryKey: ['prebook-calculate-price', params],
    enabled: !!params?.providerId && !!params?.patientId && !!params?.locationId,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }): Promise<CalculatePriceResult> => {
      if (!params) throw new Error('useCalculatePrice called without params');
      // Match Flutter: pass `null` through, don't coerce to empty string —
      // backend 400s on `billingProfileId: ""` / `sponsorId: ""`.
      const body = {
        billingProfileId: params.billingProfileId ?? null,
        locationId: params.locationId,
        patientId: params.patientId,
        sponsorId: params.sponsorId ?? null,
        paymentType: params.paymentType,
        providerId: params.providerId,
        individualServices: params.individualServices ?? [],
      };
      const raw = await post<RawCalculatePriceResponse>(
        '/api/v1/Practice/PreBookCalculatePrice',
        body,
        { signal },
      );
      return {
        grossTotal: raw.gross,
        discountTotal: raw.discount,
        netTotal: raw.net,
        patientPayable: raw.payable,
        sponsorOutstanding: raw.sponsorAmount,
        coPaymentWarning: toCoPaymentWarning(raw.coPaymentWarning),
      };
    },
  });
}
